
import { defineComponent, PropType } from 'vue'
import ActivateRepositoryTargetMutation from '~/apollo/mutations/dependency/activateRepositoryTarget.gql'

import { fromNow } from '~/utils/date'
import { getPackageManagerIcon, getTargetTitle } from '~/utils/dependencies'
import { extractHashId } from '~/utils/hashId'
import { shortenLargeNumber } from '~/utils/string'

import {
  type RepositoryTarget,
  RepositoryTargetSource,
  RepositoryTargetStatus
} from '~/types/types'

export default defineComponent({
  name: 'DependencyTargetCard',
  props: {
    id: {
      type: String as PropType<RepositoryTarget['id']>,
      required: true
    },
    packageManager: {
      type: String as PropType<RepositoryTarget['packageManager']>,
      required: true
    },
    manifestPath: {
      type: String as PropType<RepositoryTarget['manifestPath']>,
      required: true
    },
    lockfilePath: {
      type: String as PropType<RepositoryTarget['lockfilePath']>,
      required: true
    },
    isActivated: {
      type: Boolean as PropType<RepositoryTarget['isActivated']>,
      required: true
    },
    status: {
      type: String as PropType<RepositoryTarget['status']>,
      required: true
    },
    updatedAt: {
      type: String as PropType<RepositoryTarget['createdAt']>,
      default: ''
    },
    source: {
      type: String as PropType<RepositoryTarget['source']>,
      default: ''
    },
    errors: {
      type: Array as PropType<RepositoryTarget['errors']>,
      default: ''
    },
    directDependencyCount: {
      type: Number as PropType<RepositoryTarget['directDependencyCount']>,
      default: 0
    },
    transitiveDependencyCount: {
      type: Number as PropType<RepositoryTarget['transitiveDependencyCount']>,
      default: 0
    },
    lowSeverityVulnerabilityCount: {
      type: Number as PropType<RepositoryTarget['lowSeverityVulnerabilityCount']>,
      default: 0
    },
    mediumSeverityVulnerabilityCount: {
      type: Number as PropType<RepositoryTarget['mediumSeverityVulnerabilityCount']>,
      default: 0
    },
    highSeverityVulnerabilityCount: {
      type: Number as PropType<RepositoryTarget['highSeverityVulnerabilityCount']>,
      default: 0
    },
    criticalSeverityVulnerabilityCount: {
      type: Number as PropType<RepositoryTarget['criticalSeverityVulnerabilityCount']>,
      default: 0
    }
  },
  computed: {
    RepositoryTargetStatus() {
      return RepositoryTargetStatus
    },
    tooltipContent() {
      const paths = []
      if (this.manifestPath) paths.push(this.manifestPath)
      if (this.lockfilePath) paths.push(this.lockfilePath)
      return paths.join(' / ')
    },
    targetTitle() {
      return getTargetTitle(this.manifestPath, this.lockfilePath)
    },
    showLockfileDirectory() {
      return (
        this.lockfilePath &&
        this.targetTitle.lockfileDirectory !== this.targetTitle.manifestDirectory
      )
    },
    manifestTextColorClass() {
      return this.manifestPath ? 'text-vanilla-300' : ''
    },
    lockfileTextColorClass() {
      return this.lockfilePath ? (this.manifestPath ? 'text-vanilla-400' : 'text-vanilla-300') : ''
    },
    hasErrored() {
      return Boolean(this.errors.length)
    },
    latestError() {
      return this.errors.filter((error) => error && error.level === 0).pop()
    },
    vulnerabilityCounts() {
      const countArray = []

      if (this.criticalSeverityVulnerabilityCount)
        countArray.push({
          icon: 'severity-critical',
          count: shortenLargeNumber(this.criticalSeverityVulnerabilityCount),
          color: 'text-cherry'
        })

      if (this.highSeverityVulnerabilityCount)
        countArray.push({
          icon: 'severity-high',
          count: shortenLargeNumber(this.highSeverityVulnerabilityCount),
          color: 'text-amber'
        })

      if (this.mediumSeverityVulnerabilityCount)
        countArray.push({
          icon: 'severity-medium',
          count: shortenLargeNumber(this.mediumSeverityVulnerabilityCount),
          color: 'text-honey'
        })

      if (this.lowSeverityVulnerabilityCount)
        countArray.push({
          icon: 'severity-low',
          count: shortenLargeNumber(this.lowSeverityVulnerabilityCount),
          color: 'text-vanilla-400'
        })

      return countArray
    },
    metaDataItems() {
      const items = []

      if (this.isActivated) {
        items.push({
          icon: 'clock',
          label: this.updatedAt ? `Updated ${fromNow(this.updatedAt)}` : 'Waiting...'
        })

        if (this.status === RepositoryTargetStatus.Active && !this.errors.length) {
          items.push({
            icon: 'package',
            label: `${shortenLargeNumber(this.directDependencyCount)} direct dependencies`
          })
          items.push({
            icon: 'package-plus',
            label: `${shortenLargeNumber(this.transitiveDependencyCount)} transitive dependencies`
          })
        }
      } else {
        items.push({
          icon: 'focus-target',
          label:
            this.source === RepositoryTargetSource.Auto
              ? 'Automatically detected'
              : 'Manually added'
        })
      }

      return items
    }
  },
  methods: {
    getPackageManagerIcon,
    extractHashId,
    async handleActivateAnalysis() {
      try {
        await this.$applyGraphqlMutation(ActivateRepositoryTargetMutation, {
          input: {
            targetId: this.id
          }
        })
        this.$toast.success('Analysis activated successfully!')
        this.$emit('activated')
      } catch (e) {
        const message = ((e as Error)?.message || '').replace(/^GraphQL error: /, '')
        const BILLING_ERROR_PHRASE = 'Cannot bill'
        const TARGET_LIMIT_PHRASE = 'All SCA targets have been used'

        if (message.includes(BILLING_ERROR_PHRASE)) {
          this.$toast.show({
            type: 'danger',
            message:
              'You have exhausted your SCA target quota. Please purchase more targets or upgrade your plan.',
            timeout: 10
          })
        } else if (message.includes(TARGET_LIMIT_PHRASE)) {
          this.$toast.show({
            type: 'danger',
            message:
              "You've reached your SCA target limit. Please contact your account manager to add more.",
            timeout: 10
          })
        } else {
          this.$logErrorAndToast(e as Error, 'An error occurred while activating target.')
        }
      }
    }
  }
})
